import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { ColumnsConfig, TableConfig, TableData } from 'sct-custom-table/sct-table/projects/sct-table/src/lib/custom-table-interface';
import { NotificationMessageService } from 'src/app/shared/notification-message/notification-message.service';
import { SitesService } from 'src/app/sites/sites.service';
import { UsersService } from 'src/app/users/users.service';
import { AdminService } from '../admin.service';

type DemoSitePair = {
	id: number,
	source_site_id: number,
	source_site_name: string,
	source_customer_id: number,
	target_site_id: number,
	target_site_name: string,
	target_customer_id: number,
	refresh_requested_at: string,
	remove_requested_at: string
};

/**
 * Which source site is copied onto which demo site, for the DEMO_copyDevicesList schedule.
 *
 * The pairs used to be a constant in demoDAO, so adding a demo site meant a deploy. They now live
 * in demo_site_pairs, and since every environment has its own database this page configures the
 * environment it is opened on and nothing else.
 */
@Component({
	selector: 'app-demo-site-pairs',
	templateUrl: './demo-site-pairs.component.html',
	styleUrls: ['./demo-site-pairs.component.css']
})
export class DemoSitePairsComponent implements OnInit {
	@ViewChild('addPairModal') addPairModal;
	@ViewChild('deletePairModal') deletePairModal;
	@ViewChild('refreshPairModal') refreshPairModal;
	@ViewChild('removePairModal') removePairModal;

	/** savePairStatus in DAO/demoDAO.js - keep the two in step. */
	private readonly savePairStatus = {
		ok: 1,
		invalidSites: 2,
		sameSite: 3,
		duplicatePair: 4,
		targetIsASource: 5,
		sourceIsATarget: 6,
		sourceAlreadyCopied: 7
	};

	pairsTableData: TableData[] = [];
	sitesList: {id: number, name: string}[] = [];
	newPair: {sourceSiteId: number, targetSiteId: number} = {sourceSiteId: null, targetSiteId: null};
	saveError = '';
	selectedPair: DemoSitePair = null;

	tableConfig: TableConfig = {
		hasPagination: false,
		fitScreen: false
	};

	columnConfig: ColumnsConfig[] = [
		{ key: 'source_site', name: this.translateService.instant('demo_site_pairs.source_site'), type: 'link', hasSort: false },
		{ key: 'demo_site', name: this.translateService.instant('demo_site_pairs.demo_site'), type: 'link', hasSort: false },
		{ key: 'pending_action', name: this.translateService.instant('demo_site_pairs.pending_action'), type: 'string', hasSort: false },
		{ key: 'actions', name: this.translateService.instant('g.action'), type: 'dropdown' }
	];

	constructor(
		private adminService: AdminService,
		private sitesService: SitesService,
		private usersService: UsersService,
		private notificationMessage: NotificationMessageService,
		private router: Router,
		private translateService: TranslateService
	) {}

	ngOnInit() {
		if (!this.usersService.hasAccessFunction('super_admin'))
			return this.router.navigate(['/unauthorized']);

		this.getSitesList();
		return this.getDemoSitePairs();
	}

	ngAfterViewInit() {
		this.deletePairModal.onClose.subscribe((confirmed) => {
			if (confirmed)
				return this.deletePair();
			return this.selectedPair = null;
		});

		this.refreshPairModal.onClose.subscribe((confirmed) => {
			if (confirmed)
				return this.requestRefresh();
			return this.selectedPair = null;
		});

		this.removePairModal.onClose.subscribe((confirmed) => {
			if (confirmed)
				return this.requestRemoval();
			return this.selectedPair = null;
		});
	}

	getSitesList() {
		this.sitesService.getUserSitesInfo({getSitesInfoOnly: true}).subscribe((res: any) => {
			this.sitesList = (res?.sitesInfo || []).map(({id, name, customer_name}) => ({
				id,
				name: `${customer_name} | ${name}`
			}));
		});
	}

	getDemoSitePairs() {
		this.pairsTableData = [];
		this.adminService.getDemoSitePairs().subscribe((res: any) => {
			this.pairsTableData = (res || []).map((pair: DemoSitePair) => this.buildPairRow(pair));
		});
	}

	private buildPairRow(pair: DemoSitePair): TableData {
		// A site deleted after the pair was added leaves the join empty, so fall back to the raw id
		const sourceName = pair.source_site_name || `#${pair.source_site_id}`,
			targetName = pair.target_site_name || `#${pair.target_site_id}`;

		return {
			source_site: { value: sourceName, link: ['/', pair.source_customer_id, pair.source_site_id] },
			demo_site: { value: targetName, link: ['/', pair.target_customer_id, pair.target_site_id] },
			pending_action: { value: this.getPendingLabel(pair) },
			// A pair already on its way out offers nothing but the wait: the next run deletes it
			actions: { value: null, options: pair.remove_requested_at ? [] : [
				{ text: this.translateService.instant('demo_site_pairs.refresh_data'), action: () => this.showRefreshPairModal(pair) },
				{ text: this.translateService.instant('demo_site_pairs.delete_pair'), action: () => this.showDeletePairModal(pair) },
				{ text: this.translateService.instant('demo_site_pairs.remove_pair'), action: () => this.showRemovePairModal(pair) }
			] }
		};
	}

	private getPendingLabel(pair: DemoSitePair): string {
		if (pair.remove_requested_at)
			return this.translateService.instant('demo_site_pairs.removal_pending', {
				time: moment(pair.remove_requested_at).format('MM/DD/YYYY HH:mm')
			});

		if (pair.refresh_requested_at)
			return this.translateService.instant('demo_site_pairs.refresh_pending', {
				time: moment(pair.refresh_requested_at).format('MM/DD/YYYY HH:mm')
			});

		return '-';
	}

	showAddPairModal() {
		this.newPair = {sourceSiteId: null, targetSiteId: null};
		this.saveError = '';
		this.addPairModal.show();
	}

	hideAddPairModal() {
		this.newPair = {sourceSiteId: null, targetSiteId: null};
		this.saveError = '';
		this.addPairModal.hide();
	}

	savePair() {
		// Shown inside the modal, not through notificationMessage: the modal is still open and its
		// backdrop covers the page-level banner, so the reason the save failed would be hidden.
		this.saveError = '';

		if (!this.newPair.sourceSiteId || !this.newPair.targetSiteId)
			return this.saveError = this.translateService.instant('demo_site_pairs.invalid_sites');

		return this.adminService.saveDemoSitePair(this.newPair.sourceSiteId, this.newPair.targetSiteId).subscribe((res: any) => {
			const errorKey = this.getSaveErrorKey(res);
			if (errorKey)
				return this.saveError = this.translateService.instant(errorKey);

			this.notificationMessage.setMessage('globalSuccessMsg');
			this.hideAddPairModal();
			return this.getDemoSitePairs();
		});
	}

	private getSaveErrorKey(status: number): string {
		switch (status) {
			case this.savePairStatus.sameSite:
				return 'demo_site_pairs.same_site';
			case this.savePairStatus.duplicatePair:
				return 'demo_site_pairs.duplicate_pair';
			case this.savePairStatus.targetIsASource:
				return 'demo_site_pairs.target_is_a_source';
			case this.savePairStatus.sourceIsATarget:
				return 'demo_site_pairs.source_is_a_target';
			case this.savePairStatus.sourceAlreadyCopied:
				return 'demo_site_pairs.source_already_copied';
			case this.savePairStatus.ok:
				return null;
			default:
				return 'demo_site_pairs.invalid_sites';
		}
	}

	showDeletePairModal(pair: DemoSitePair) {
		this.selectedPair = pair;
		this.deletePairModal.show();
	}

	deletePair() {
		return this.adminService.deleteDemoSitePair(this.selectedPair.id).subscribe(() => {
			this.notificationMessage.setMessage('globalSuccessMsg');
			this.selectedPair = null;
			return this.getDemoSitePairs();
		});
	}

	showRefreshPairModal(pair: DemoSitePair) {
		this.selectedPair = pair;
		this.refreshPairModal.show();
	}

	requestRefresh() {
		return this.adminService.requestDemoSitePairRefresh(this.selectedPair.id).subscribe(() => {
			this.notificationMessage.setMessage('translate|demo_site_pairs.refresh_requested', {timeout: 20000});
			this.selectedPair = null;
			return this.getDemoSitePairs();
		});
	}

	showRemovePairModal(pair: DemoSitePair) {
		this.selectedPair = pair;
		this.removePairModal.show();
	}

	requestRemoval() {
		return this.adminService.requestDemoSitePairRemoval(this.selectedPair.id).subscribe(() => {
			this.notificationMessage.setMessage('translate|demo_site_pairs.removal_requested', {timeout: 20000});
			this.selectedPair = null;
			return this.getDemoSitePairs();
		});
	}

	get selectedSourceName(): string {
		return this.selectedPair?.source_site_name || `#${this.selectedPair?.source_site_id}`;
	}

	get selectedTargetName(): string {
		return this.selectedPair?.target_site_name || `#${this.selectedPair?.target_site_id}`;
	}
}
